import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, differenceInDays, addDays, formatISO } from "date-fns";
import { ArrowUp, ArrowDown, InfoIcon } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { UserSubscription, SubscriptionPlan } from "@/types/subscription";

// Define the form schema
const upgradeSubscriptionSchema = z.object({
  newPlanId: z.number().min(1, "Please select a plan"),
  applyProrating: z.boolean().default(true),
  adminNotes: z.string().optional(),
  keepCurrentEndDate: z.boolean().default(true),
});

type UpgradeSubscriptionFormValues = z.infer<typeof upgradeSubscriptionSchema>;

interface UpgradeSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    data: UpgradeSubscriptionFormValues & {
      proratedCredit: number | null;
      newPrice: number;
      newCurrencyCode: string;
      newEndDate: Date | null;
    }
  ) => void;
  subscription: UserSubscription;
  availablePlans: SubscriptionPlan[];
  isProcessing: boolean;
  userName: string;
}

export default function UpgradeSubscriptionDialog({
  open,
  onOpenChange,
  onConfirm,
  subscription,
  availablePlans,
  isProcessing,
  userName,
}: UpgradeSubscriptionDialogProps) {
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null
  );
  const [proratedCredit, setProratedCredit] = useState<number | null>(null);
  const [newPrice, setNewPrice] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [newEndDate, setNewEndDate] = useState<Date | null>(null);

  // Initialize the form
  const form = useForm<UpgradeSubscriptionFormValues>({
    resolver: zodResolver(upgradeSubscriptionSchema),
    defaultValues: {
      newPlanId: 0,
      applyProrating: true,
      adminNotes: `Plan change for ${userName}`,
      keepCurrentEndDate: true,
    },
  });

  // Calculate remaining days on current subscription
  const calculateRemainingDays = (): number => {
    if (!subscription.subscriptionEnd) return 0;

    const now = new Date();
    const endDate = new Date(subscription.subscriptionEnd);

    // If subscription has already ended, return 0
    if (endDate <= now) return 0;

    return differenceInDays(endDate, now);
  };

  // Calculate prorated credit for remaining days on current subscription
  const calculateProratedCredit = (): number => {
    const remainingDays = calculateRemainingDays();
    if (remainingDays <= 0 || !subscription.subscriptionEnd) return 0;

    const startDate = new Date(subscription.subscriptionStart);
    const endDate = new Date(subscription.subscriptionEnd);
    const totalDays = Math.max(1, differenceInDays(endDate, startDate));

    const dailyRate = subscription.price / totalDays;
    return Math.round(dailyRate * remainingDays * 100) / 100;
  };

  // Calculate new end date based on selected plan
  const calculateNewEndDate = (
    plan: SubscriptionPlan,
    keepCurrentEndDate: boolean
  ): Date => {
    if (keepCurrentEndDate && subscription.subscriptionEnd) {
      return new Date(subscription.subscriptionEnd);
    }

    const now = new Date();

    // Calculate end date based on plan duration type
    switch (plan.durationType) {
      case "days":
        return addDays(now, plan.duration);
      case "months":
        // Simplification - use 30 days per month for calculation
        return addDays(now, plan.duration * 30);
      case "years":
        // Simplification - use 365 days per year for calculation
        return addDays(now, plan.duration * 365);
      default:
        return addDays(now, 30); // Default to 30 days
    }
  };

  // Handle plan selection
  const handlePlanChange = (planId: number) => {
    const plan = availablePlans.find((p) => p.id === planId);
    if (!plan) return;

    setSelectedPlan(plan);
    form.setValue("newPlanId", plan.id);

    // Find matching price configuration for the current currency
    const priceConfig = plan.priceConfigurations?.find(
      (pc) => pc.currencyCode === subscription.currencyCode
    );

    if (priceConfig) {
      const planPrice = priceConfig.price;
      setNewPrice(planPrice);

      // Calculate prorated credit for current subscription
      const credit = form.watch("applyProrating")
        ? calculateProratedCredit()
        : 0;
      setProratedCredit(credit);

      // Calculate total price (new price - credit)
      setTotalPrice(Math.max(0, planPrice - credit));

      // Calculate new end date
      const endDate = calculateNewEndDate(
        plan,
        form.watch("keepCurrentEndDate")
      );
      setNewEndDate(endDate);
    }
  };

  // Handle prorating toggle
  const handleProratingToggle = (apply: boolean) => {
    form.setValue("applyProrating", apply);

    if (apply && selectedPlan) {
      const credit = calculateProratedCredit();
      setProratedCredit(credit);
      setTotalPrice(Math.max(0, newPrice - credit));
    } else {
      setProratedCredit(0);
      setTotalPrice(newPrice);
    }
  };

  // Handle keep current end date toggle
  const handleKeepEndDateToggle = (keep: boolean) => {
    form.setValue("keepCurrentEndDate", keep);

    if (selectedPlan) {
      const endDate = calculateNewEndDate(selectedPlan, keep);
      setNewEndDate(endDate);
    }
  };

  // Handle form submission
  const onSubmit = (data: UpgradeSubscriptionFormValues) => {
    if (!selectedPlan || !newEndDate) return;

    onConfirm({
      ...data,
      proratedCredit: data.applyProrating ? proratedCredit : null,
      newPrice,
      newCurrencyCode: subscription.currencyCode,
      newEndDate: data.keepCurrentEndDate
        ? subscription.subscriptionEnd
          ? new Date(subscription.subscriptionEnd)
          : null
        : newEndDate,
    });
  };

  // Determine if this is an upgrade, downgrade, or lateral move
  const determinePlanChangeType = (
    plan: SubscriptionPlan | null
  ): "upgrade" | "downgrade" | "lateral" | null => {
    if (!plan || !subscription.subscriptionPlan) return null;

    // Find price config for the current currency
    const newPlanPrice = plan.priceConfigurations?.find(
      (pc) => pc.currencyCode === subscription.currencyCode
    )?.price;

    const currentPlanPrice = subscription.price;

    if (!newPlanPrice) return null;

    if (newPlanPrice > currentPlanPrice) {
      return "upgrade";
    } else if (newPlanPrice < currentPlanPrice) {
      return "downgrade";
    } else {
      return "lateral";
    }
  };

  // Get change type details
  const getPlanChangeDetails = () => {
    const changeType = determinePlanChangeType(selectedPlan);

    switch (changeType) {
      case "upgrade":
        return {
          label: "Upgrade",
          icon: <ArrowUp className="h-4 w-4 text-green-500" />,
          description: "Higher tier plan with more features",
          color: "text-green-600",
        };
      case "downgrade":
        return {
          label: "Downgrade",
          icon: <ArrowDown className="h-4 w-4 text-amber-500" />,
          description: "Lower tier plan with fewer features",
          color: "text-amber-600",
        };
      case "lateral":
        return {
          label: "Plan Change",
          icon: null,
          description: "Different plan at the same price point",
          color: "text-blue-600",
        };
      default:
        return {
          label: "Plan Change",
          icon: null,
          description: "Select a new plan",
          color: "",
        };
    }
  };

  const changeDetails = getPlanChangeDetails();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {changeDetails.icon}
            {changeDetails.label} Subscription
          </DialogTitle>
          <DialogDescription>
            Change {userName}'s subscription from{" "}
            {subscription.subscriptionPlan?.name} to a different plan.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Current Plan Section */}
            <div className="bg-muted/30 p-3 rounded-md mb-4">
              <h3 className="text-sm font-medium mb-2">Current Plan</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Plan:</span>{" "}
                  <span className="font-medium">
                    {subscription.subscriptionPlan?.name}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Price:</span>{" "}
                  <span className="font-medium">
                    {subscription.price} {subscription.currencyCode}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">End Date:</span>{" "}
                  <span className="font-medium">
                    {subscription.subscriptionEnd
                      ? format(new Date(subscription.subscriptionEnd), "PPP")
                      : "No end date (perpetual)"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Remaining:</span>{" "}
                  <span className="font-medium">
                    {calculateRemainingDays()} days
                  </span>
                </div>
              </div>
            </div>

            {/* Plan Selection */}
            <FormField
              control={form.control}
              name="newPlanId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Subscription Plan</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      handlePlanChange(parseInt(value, 10))
                    }
                    defaultValue={field.value ? field.value.toString() : ""}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a plan" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availablePlans
                        .filter(
                          (plan) => plan.id !== subscription.subscriptionPlanId
                        )
                        .map((plan) => (
                          <SelectItem key={plan.id} value={plan.id.toString()}>
                            {plan.name} -{" "}
                            {plan.priceConfigurations?.find(
                              (pc) =>
                                pc.currencyCode === subscription.currencyCode
                            )?.price || "N/A"}{" "}
                            {subscription.currencyCode}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Choose the new plan for this subscription
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedPlan && (
              <>
                {/* Plan Change Type */}
                <div className="rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    {changeDetails.icon}
                    <span className={`font-medium ${changeDetails.color}`}>
                      {changeDetails.label}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {changeDetails.description}
                  </p>
                </div>

                {/* End Date Options */}
                <FormField
                  control={form.control}
                  name="keepCurrentEndDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 space-y-0">
                      <div className="space-y-0.5">
                        <FormLabel>Keep Current End Date</FormLabel>
                        <FormDescription>
                          Maintain the same expiration date as the current
                          subscription
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) =>
                            handleKeepEndDateToggle(checked)
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Billing Preview */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-base font-medium mb-3">
                      Billing Preview
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span>New Plan Price:</span>
                        <span className="font-medium">
                          {newPrice} {subscription.currencyCode}
                        </span>
                      </div>

                      {/* Prorating Options */}
                      <FormField
                        control={form.control}
                        name="applyProrating"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 space-y-0">
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1">
                                <FormLabel>Apply Prorated Credit</FormLabel>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <InfoIcon className="h-4 w-4 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="w-[250px] text-sm">
                                        If enabled, the remaining value of the
                                        current subscription will be applied as
                                        credit toward the new plan.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <FormDescription>
                                Credit unused time from current subscription
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={(checked) =>
                                  handleProratingToggle(checked)
                                }
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {proratedCredit !== null &&
                        proratedCredit > 0 &&
                        form.watch("applyProrating") && (
                          <div className="flex items-center justify-between text-green-600">
                            <span>Prorated Credit:</span>
                            <span>
                              -{proratedCredit} {subscription.currencyCode}
                            </span>
                          </div>
                        )}

                      <Separator />

                      <div className="flex items-center justify-between font-bold">
                        <span>Total Due Now:</span>
                        <span>
                          {totalPrice} {subscription.currencyCode}
                        </span>
                      </div>

                      <div className="text-sm text-muted-foreground">
                        <span>New end date: </span>
                        <span className="font-medium">
                          {newEndDate
                            ? format(newEndDate, "PPP")
                            : subscription.subscriptionEnd
                            ? format(
                                new Date(subscription.subscriptionEnd),
                                "PPP"
                              )
                            : "No end date (perpetual)"}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Admin Notes */}
                <FormField
                  control={form.control}
                  name="adminNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Admin Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter any notes about this plan change"
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isProcessing || !selectedPlan || newPrice <= 0}
                className={
                  changeDetails.label === "Upgrade"
                    ? "bg-green-600 hover:bg-green-700"
                    : ""
                }
              >
                {isProcessing
                  ? "Processing..."
                  : `Confirm ${changeDetails.label}`}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
